import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { API } from "@/api";
import axios from "axios";
import { getErrorMessage, generateErrorCode } from "@/utils/errorMessages";

const ErrorBrainContext = createContext(null);

export function useErrorBrain() {
  return useContext(ErrorBrainContext);
}

export function ErrorBrainProvider({ children }) {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const clearToast = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast(null);
  }, []);

  const showError = useCallback((type, opts = {}) => {
    const msg = getErrorMessage(type);
    const fingerprint = opts.fingerprint || "";
    const errorCode = fingerprint ? generateErrorCode(fingerprint) : "ERR-0000";

    // Never stacks — replace any existing toast
    if (timerRef.current) clearTimeout(timerRef.current);

    const newToast = {
      errorCode,
      title: opts.title || msg.title,
      message: opts.message || msg.message,
      action: opts.action || msg.action,
      onAction: opts.onAction || clearToast,
      showReport: opts.showReport !== false,
      fingerprint,
      endpoint: opts.endpoint || "",
    };

    setToast(newToast);

    // Auto-dismiss after 8 seconds
    timerRef.current = setTimeout(() => {
      setToast(null);
    }, 8000);

    // Store code mapping in backend (fire and forget)
    if (fingerprint) {
      const token = localStorage.getItem("token");
      axios.post(`${API}/error-brain/generate-code`, { fingerprint }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).catch(() => {});
    }
  }, [clearToast]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return (
    <ErrorBrainContext.Provider value={{ toast, showError, clearToast }}>
      {children}
    </ErrorBrainContext.Provider>
  );
}
